import type { AccessTokenData } from '@empathyco/playboard-core-domain'
import type { JsonWebTokenPayload } from './json-web-token.service'
import { JsonWebTokenService } from './json-web-token.service'

export interface PlayAuthTokenPayload extends JsonWebTokenPayload {
  'eb::id': string
  first_name: string
  last_name: string
  'eb::roles'?: Record<string, Array<string>>
  authorities: Array<string>
}

export class PlayAuthTokenService extends JsonWebTokenService<PlayAuthTokenPayload> {
  protected getUserInfoFromPayload(payload: PlayAuthTokenPayload): AccessTokenData['user'] {
    return {
      id: payload['eb::id'],
      name: payload.sub,
      firstName: payload.first_name,
      lastName: payload.last_name,
    }
  }

  protected getAuthorizationFromPayload(
    payload: PlayAuthTokenPayload,
  ): AccessTokenData['authorization'] {
    return {
      ...this.getAuthorizationResourcesFromPayload(payload),
      ...this.getAuthorizationRolesFromPayload(payload),
    }
  }

  private getAuthorizationResourcesFromPayload(
    payload: PlayAuthTokenPayload,
  ): Pick<AccessTokenData['authorization'], 'resources'> {
    const resources = payload.authorities?.reduce<AccessTokenData['authorization']['resources']>(
      (result, authority) => {
        const [instance, name, accessLevel] = authority.split(':')
        let resource = result.find(resource => resource.name === name)
        if (!resource) {
          resource = {
            name,
            conditions: [],
          }
          result.push(resource)
        }
        const condition = resource.conditions.find(
          condition => condition['access-level'][0] === accessLevel,
        )
        if (condition) {
          condition.instance.push(instance)
        } else {
          resource.conditions.push({
            instance: [instance],
            'access-level': [accessLevel],
          })
        }
        return result
      },
      [],
    )
    return {
      resources,
    }
  }

  private getAuthorizationRolesFromPayload(
    payload: PlayAuthTokenPayload,
  ): Pick<AccessTokenData['authorization'], 'roles'> {
    const roles = Object.entries(payload['eb::roles'] ?? {}).reduce<
      AccessTokenData['authorization']['roles']
    >((result, [role, instances]) => {
      instances.forEach(instance => {
        if (!result.hasOwnProperty(instance)) {
          result[instance] = []
        }
        result[instance].push(role)
      })
      return result
    }, {})
    return {
      roles,
    }
  }
}
